import {
  isSplitOf,
  type PaymentMode,
  type SalePayment,
  type SalePaymentPart,
} from '@repo/desktop-bridge';
import { z } from 'zod';

import { formatPaise, paiseFromRupees, RUPEES_SHAPE } from '@/lib/money';

import {
  PAYMENT_MODES,
  paymentModeLabel,
  paymentReferenceField,
  paymentReferenceOnWire,
} from './payment-modes';

/**
 * One bill paid two or three ways (#357) — a **split payment**, on both
 * counters' payment form. Never "split party": at the gate that already means a
 * basket split between an invoice and tickets.
 *
 * **An exception, not a new step.** A bill paid one way is one tap on a mode, as
 * it always was, and goes to the till exactly as it always did. A split is asked
 * for in a dialog of its own (`SplitPaymentDialog`), and lands on the payment
 * form as `payments` in place of a mode. The rules are the API's, held in the
 * bridge (`isSplitOf`) so the screen and the main process apply one copy.
 */

/**
 * The split as the payment form holds it: two or three parts, one per mode,
 * each a whole number of paise above nothing. Set by the dialog, never typed.
 * What it must add up to moves with the basket, so Take payment checks that.
 */
export const splitPaymentField = z.custom<readonly SalePaymentPart[]>(
  (value) =>
    Array.isArray(value) &&
    isSplitOf(
      value as SalePaymentPart[],
      (value as SalePaymentPart[]).reduce((sum, part) => sum + part.amountPaise, 0),
    ),
  { message: 'Split the payment again.' },
);

/** Whether they have said how they paid: one mode, or a split. Both forms refine on it. */
export const choseHowTheyPaid = (values: {
  paymentMode?: PaymentMode | undefined;
  payments?: readonly SalePaymentPart[] | undefined;
}): boolean => values.paymentMode !== undefined || values.payments !== undefined;

/**
 * The payment as it goes to the till: the parts of a split, or — **exactly as
 * before #357** — a mode and its reference, the reference omitted where there
 * is none or the mode is cash.
 */
export function paymentOnRequest(values: {
  paymentMode?: PaymentMode | undefined;
  paymentReference?: string | undefined;
  payments?: readonly SalePaymentPart[] | undefined;
}): SalePayment {
  if (values.payments !== undefined) return { payments: values.payments };

  // Unreachable past the form's schema, which refuses neither; thrown rather
  // than guessed, because a guessed mode is a drawer short by the bill.
  if (values.paymentMode === undefined) throw new Error('a payment with no mode and no split');

  const reference = paymentReferenceOnWire(values.paymentMode, values.paymentReference);

  return {
    paymentMode: values.paymentMode,
    ...(reference === undefined ? {} : { paymentReference: reference }),
  };
}

/** Rupees as typed into a part, or empty for a mode that took nothing. */
const partAmount = z
  .string()
  .trim()
  .refine((value) => value === '' || RUPEES_SHAPE.test(value), {
    message: 'Enter an amount, like 500 or 500.50.',
  })
  .refine((value) => !RUPEES_SHAPE.test(value) || paiseFromRupees(value) > 0, {
    message: 'Leave it empty rather than ₹0.',
  });

/**
 * The dialog's form: an amount for each mode, a reference for UPI and card,
 * and the two rules about the whole — at least two parts, adding up to this
 * bill. A mode twice cannot be typed: there is one field per mode.
 *
 * The whole-split refusals are on `split`, a name no field takes, so they render
 * above the dialog's submit rather than against one amount.
 */
export const splitFormSchema = (totalPaise: number) =>
  z
    .object({
      CASH: partAmount,
      UPI: partAmount,
      CARD: partAmount,
      UPI_REFERENCE: paymentReferenceField,
      CARD_REFERENCE: paymentReferenceField,
      // Nothing is typed here: it is where the whole split's refusals land.
      split: z.undefined().optional(),
    })
    .superRefine((values, context) => {
      const parts = partsFromSplitForm(values);
      const sum = parts.reduce((total, part) => total + part.amountPaise, 0);

      if (parts.length < 2) {
        context.addIssue({
          code: 'custom',
          path: ['split'],
          message: 'Split it across at least two ways to pay.',
        });
      } else if (sum !== totalPaise) {
        context.addIssue({
          code: 'custom',
          path: ['split'],
          message: `The parts come to ${formatPaise(sum)}. The bill is ${formatPaise(totalPaise)}.`,
        });
      }
    });

export type SplitFormValues = z.infer<ReturnType<typeof splitFormSchema>>;

/** Where each mode's reference is typed. Cash has none. */
export const REFERENCE_FIELD = { UPI: 'UPI_REFERENCE', CARD: 'CARD_REFERENCE' } as const;

/**
 * The parts the dialog's fields say, cash first: a mode with an amount, and its
 * reference where it may carry one. A reference typed against a mode left
 * empty goes with it.
 */
export function partsFromSplitForm(values: {
  readonly CASH?: string | undefined;
  readonly UPI?: string | undefined;
  readonly CARD?: string | undefined;
  readonly UPI_REFERENCE?: string | undefined;
  readonly CARD_REFERENCE?: string | undefined;
}): SalePaymentPart[] {
  return PAYMENT_MODES.flatMap(({ mode }): SalePaymentPart[] => {
    const amount = values[mode]?.trim() ?? '';

    if (amount === '' || !RUPEES_SHAPE.test(amount)) return [];

    const reference =
      mode === 'CASH' ? undefined : paymentReferenceOnWire(mode, values[REFERENCE_FIELD[mode]]);

    return [
      {
        paymentMode: mode,
        amountPaise: paiseFromRupees(amount),
        ...(reference === undefined ? {} : { paymentReference: reference }),
      },
    ];
  });
}

/** What a split's parts add up to. */
export const splitTotal = (parts: readonly SalePaymentPart[]): number =>
  parts.reduce((sum, part) => sum + part.amountPaise, 0);

/** `Cash ₹500.00 + UPI ₹1,240.00`, in the counter's order. */
export const splitLabel = (parts: readonly SalePaymentPart[]): string =>
  PAYMENT_MODES.flatMap(({ mode }) =>
    parts
      .filter((part) => part.paymentMode === mode)
      .map((part) => `${paymentModeLabel(mode)} ${formatPaise(part.amountPaise)}`),
  ).join(' + ');

/**
 * How the sales a payment made were paid, said back to the cashier: the mode,
 * or each mode and what it took — across every sale a party across outlets
 * made (#284), since one split may have been shared out between them.
 */
export function paidByLabel(
  sales: readonly {
    readonly paymentMode: PaymentMode;
    readonly totalPaise: number;
    readonly payments?: readonly SalePaymentPart[] | undefined;
  }[],
): string {
  const taken = new Map<PaymentMode, number>();

  for (const sale of sales) {
    for (const part of sale.payments ?? [
      { paymentMode: sale.paymentMode, amountPaise: sale.totalPaise },
    ]) {
      taken.set(part.paymentMode, (taken.get(part.paymentMode) ?? 0) + part.amountPaise);
    }
  }

  const [only] = taken.keys();

  return taken.size === 1 && only !== undefined
    ? paymentModeLabel(only)
    : splitLabel([...taken].map(([paymentMode, amountPaise]) => ({ paymentMode, amountPaise })));
}
